"use client";

import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import { useGtmProductViewTracking } from "@/hooks/use-gtm-product-detail-tracking";
import { ProductMediaGallery } from "@/scott/ui/blocks/product-media-gallery/product-media-gallery";
import type { Dictionary } from "@/utils/dictionaries/get-dictionary";
import type { ProductHeroEntry } from "../product-detail-page.adapter";
import type { stockNotificationAction } from "../stock-notification-action";
import { ProductSummaryPanel } from "./product-summary-panel";
import {
  type VariantSwitchHandler,
  VariantSwitchProvider,
} from "./product-variant-switch-context";

export interface ProductVariantSwitcherProps {
  /** Every selectable article, keyed by its code. */
  heroesByCode: Record<string, ProductHeroEntry>;
  initialArticleCode: string;
  dictionary: Dictionary["productDetailPage"];
  country: string;
  isEcommerce: boolean;
  language: string;
  newBadgeLabel?: string;
  stockNotificationAction: typeof stockNotificationAction;
}

/**
 * Holds the selected article in client state so switching colour/size swaps the
 * hero (gallery + summary panel) instantly, with no RSC navigation and
 * therefore no per-variant catalogue request. The address bar is kept in sync
 * via `history.replaceState` so the URL stays shareable and reloads resolve to
 * the same variant server-side.
 */
export function ProductVariantSwitcher(
  props: Readonly<ProductVariantSwitcherProps>
) {
  const router = useRouter();
  const [selectedCode, setSelectedCode] = useState(props.initialArticleCode);

  // Follow the URL after a same-page navigation: when the server re-renders with
  // a different initial article (e.g. back/forward, or a link to another
  // variant), reset the local selection to it. In-page swatch clicks still
  // update the selection between navigations.
  const [lastInitialArticleCode, setLastInitialArticleCode] = useState(
    props.initialArticleCode
  );
  if (props.initialArticleCode !== lastInitialArticleCode) {
    setLastInitialArticleCode(props.initialArticleCode);
    setSelectedCode(props.initialArticleCode);
  }

  const current =
    props.heroesByCode[selectedCode] ??
    props.heroesByCode[props.initialArticleCode];

  // view_item reports the article the page loaded with, once per PDP load — not
  // the article the shopper later switches to in-page.
  const initialEntry =
    props.heroesByCode[props.initialArticleCode] ??
    props.heroesByCode[selectedCode];
  useGtmProductViewTracking(initialEntry?.product.gtm);

  const handleSelect = useCallback<VariantSwitchHandler>(
    (articleCode, href) => {
      // Variants whose below-the-hero content differs are not in the map; fall
      // back to a full navigation so the whole PDP updates for those articles.
      if (!props.heroesByCode[articleCode]) {
        if (href) {
          router.push(href, { scroll: false });
        }
        return;
      }

      setSelectedCode(articleCode);

      if (href && typeof window !== "undefined") {
        window.history.replaceState(window.history.state, "", href);
      }
    },
    [props.heroesByCode, router]
  );

  if (!current) {
    return null;
  }

  const { product } = current;

  return (
    <VariantSwitchProvider value={handleSelect}>
      <div className="min-w-0">
        <ProductMediaGallery
          key={JSON.stringify(product.mediaGallery)}
          microcopies={{
            viewGallery: "View gallery",
            previous: "Previous",
            next: "Next",
            close: props.dictionary.close,
            imageCounter: "{current}/{total}",
            playVideo: "Play video",
          }}
          productName={product.name}
          {...product.mediaGallery}
        />
      </div>

      <ProductSummaryPanel
        country={props.country}
        dictionary={props.dictionary}
        isEcommerce={props.isEcommerce}
        language={props.language}
        newBadgeLabel={props.newBadgeLabel}
        product={product}
        sizeFinder={current.sizeFinder}
        stockNotificationAction={props.stockNotificationAction}
      />
    </VariantSwitchProvider>
  );
}
